import { CKEditor } from '@ckeditor/ckeditor5-react'
import Editor from 'ckeditor5-custom-build/build/ckeditor'
import React, { useContext, useEffect, useState } from 'react'
import { Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap'
import { AppContext } from '../context/AppContext'
import fetchData from '../utils/fetchData'
import getCookie from '../utils/getCookie'
import { staticUrl } from '../utils/url'

const emptyMeta = {
  title: '',
  description: '',
  keywords: '',
  author: '',
  properties: [{ property: '', content: '' }],
}

/**
 * CMS editor for legal/policy pages (same pattern as About).
 * @param {{ pageName: string, label: string, defaults?: { title?: string, subtitle?: string, description?: string } }} props
 */
const PolicyEditor = ({ pageName, label, defaults = {} }) => {
  const { setAlert } = useContext(AppContext)
  const [isFetching, setFetching] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)
  const [inputData, setInputData] = useState({
    title: defaults.title || '',
    subtitle: defaults.subtitle || 'Legal',
    description: defaults.description || '',
  })
  const [metaData, setMetaData] = useState(emptyMeta)

  const getPageInformation = async () => {
    setFetching(true)
    try {
      const { data } = await fetchData.get(`/page?name=${pageName}`, {
        headers: {
          Authorization: `Bearer ${getCookie('eyevaryToken')}`,
        },
      })
      if (data?.page?.metaData) {
        setMetaData({
          ...emptyMeta,
          ...data.page.metaData,
          properties:
            data.page.metaData.properties?.length > 0
              ? data.page.metaData.properties
              : emptyMeta.properties,
        })
      }
      setInputData({
        title: data?.page?.pageData?.title || defaults.title || '',
        subtitle: data?.page?.pageData?.subtitle || defaults.subtitle || 'Legal',
        description:
          data?.page?.pageData?.description || defaults.description || '',
      })
    } catch (error) {
      console.log({ ...error }, error)
      setAlert({
        show: true,
        variant: 'danger',
        message:
          error?.response?.data?.message ||
          'Something went wrong while fetching data. Please try again.',
        icon: 'mdi mdi-close',
        duration: 5,
      })
    }
    setFetching(false)
  }

  useEffect(() => {
    getPageInformation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageName])

  const handleChange = ({ target }, from = 'normal', index) => {
    const { name, value, type, checked } = target
    if (from === 'normal') {
      setInputData({
        ...inputData,
        [name]: type === 'checkbox' ? checked : value,
      })
    } else if (from === 'meta') {
      if (index >= 0) {
        metaData.properties[index][name] = value
        setMetaData({ ...metaData })
      } else setMetaData({ ...metaData, [name]: value })
    }
  }

  const uploadAdapter = (loader) => {
    return {
      upload: () =>
        new Promise((resolve, reject) => {
          const body = new FormData()
          loader.file
            .then(async (file) => {
              body.append('ckImage', file)
              try {
                const { data } = await fetchData.post('/page/ckimage', body, {
                  headers: {
                    Authorization: `Bearer ${getCookie('eyevaryToken')}`,
                  },
                })
                resolve({ default: staticUrl + data?.imageURI })
              } catch (error) {
                reject(error)
              }
            })
            .catch((error) => reject(error))
        }),
    }
  }

  function uploadPlugin(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) =>
      uploadAdapter(loader)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      const { data } = await fetchData.patch(
        `/page?name=${pageName}`,
        { pageData: inputData, metaData },
        { headers: { Authorization: `Bearer ${getCookie('eyevaryToken')}` } }
      )
      if (data?.page?.metaData) setMetaData(data.page.metaData)
      setInputData({
        title: data?.page?.pageData?.title || '',
        subtitle: data?.page?.pageData?.subtitle || 'Legal',
        description: data?.page?.pageData?.description || '',
      })
      setAlert({
        show: true,
        variant: 'success',
        message: `${label} successfully updated`,
        icon: 'mdi mdi-check',
        duration: 5,
      })
    } catch (error) {
      console.log({ ...error }, error)
      setAlert({
        show: true,
        variant: 'danger',
        message:
          error?.response?.data?.message ||
          'Something went wrong. Please try again.',
        icon: 'mdi mdi-close',
        duration: 5,
      })
    }
    setSubmitting(false)
  }

  return (
    <section className='content-wrapper'>
      <Form onSubmit={handleSubmit}>
        <Card className='mb-4'>
          <Card.Header>{label}</Card.Header>
          <Card.Body>
            {isFetching ? (
              <div className='text-center py-4'>
                <Spinner animation='border' />
              </div>
            ) : (
              <>
                <Form.Group controlId='title' className='mb-3'>
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    type='text'
                    name='title'
                    value={inputData.title}
                    onChange={(e) => handleChange(e, 'normal')}
                    required
                  />
                </Form.Group>
                <Form.Group controlId='subtitle' className='mb-3'>
                  <Form.Label>Eyebrow / Label</Form.Label>
                  <Form.Control
                    type='text'
                    name='subtitle'
                    value={inputData.subtitle}
                    onChange={(e) => handleChange(e, 'normal')}
                    placeholder='Legal'
                  />
                </Form.Group>
                <Form.Group className='mb-3'>
                  <Form.Label>Description</Form.Label>
                  <CKEditor
                    editor={Editor}
                    config={{
                      extraPlugins: [uploadPlugin],
                    }}
                    data={inputData.description}
                    onChange={(event, editor) => {
                      const description = editor.getData()
                      !isFetching && setInputData({ ...inputData, description })
                    }}
                  />
                </Form.Group>
              </>
            )}
          </Card.Body>
        </Card>
        <Card>
          <Card.Header>Meta Information</Card.Header>
          <Card.Body>
            <Form.Group controlId='meta-title' className='mb-3'>
              <Form.Label>Meta Title</Form.Label>
              <Form.Control
                type='text'
                placeholder='Enter meta title'
                name='title'
                value={metaData.title}
                onChange={(e) => handleChange(e, 'meta')}
              />
            </Form.Group>
            <Form.Group controlId='meta-description' className='mb-3'>
              <Form.Label>Meta Description</Form.Label>
              <Form.Control
                as='textarea'
                rows='5'
                placeholder='Enter meta description'
                name='description'
                value={metaData.description}
                onChange={(e) => handleChange(e, 'meta')}
              />
            </Form.Group>
            <Row>
              <Form.Group as={Col} md={6} controlId='keywords' className='mb-3'>
                <Form.Label>Meta Keyword</Form.Label>
                <Form.Control
                  type='text'
                  placeholder='Enter meta keyword'
                  name='keywords'
                  value={metaData.keywords}
                  onChange={(e) => handleChange(e, 'meta')}
                />
              </Form.Group>
              <Form.Group as={Col} md={6} controlId='author' className='mb-3'>
                <Form.Label>Meta Author</Form.Label>
                <Form.Control
                  type='text'
                  placeholder='Enter meta author'
                  name='author'
                  value={metaData.author}
                  onChange={(e) => handleChange(e, 'meta')}
                />
              </Form.Group>
            </Row>
            <p className='fw-bold mb-2'>Meta Properties</p>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th style={{ width: '3rem' }}></th>
                  <th style={{ minWidth: '15rem' }}>Property</th>
                  <th style={{ minWidth: '25rem' }}>Content</th>
                </tr>
              </thead>
              <tbody>
                {metaData.properties.map((property, i) => (
                  <tr key={i}>
                    <td
                      className='align-middle text-center pnt'
                      onClick={() => {
                        setMetaData({
                          ...metaData,
                          properties: metaData.properties.filter(
                            (p, j) => j !== i
                          ),
                        })
                      }}>
                      <span className='text-primary'>
                        <i className='mdi mdi-delete'></i>
                      </span>
                    </td>
                    <td className='align-middle'>
                      <Form.Control
                        name='property'
                        value={property.property}
                        onChange={(e) => handleChange(e, 'meta', i)}
                      />
                    </td>
                    <td className='align-middle'>
                      <Form.Control
                        name='content'
                        value={property.content}
                        onChange={(e) => handleChange(e, 'meta', i)}
                      />
                    </td>
                  </tr>
                ))}
                <tr>
                  <td
                    colSpan='3'
                    className='align-middle text-center pnt'
                    onClick={() => {
                      setMetaData({
                        ...metaData,
                        properties: [
                          ...metaData.properties,
                          { property: '', content: '' },
                        ],
                      })
                    }}>
                    <span className='text-success'>
                      <i className='mdi mdi-plus'></i>
                    </span>
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
          <Card.Footer>
            <div className='text-end'>
              <Button size='sm' type='submit' disabled={isSubmitting}>
                Submit
                {isSubmitting && (
                  <Spinner animation='border' size='sm' className='ms-2' />
                )}
              </Button>
            </div>
          </Card.Footer>
        </Card>
      </Form>
    </section>
  )
}

export default PolicyEditor
